import { baseApi, getQueryArgs, transformErrorResponse } from 'store/api'
import { ANALYTICS_BASE_HOST_NAME, ANALYTICS_BASE_URL } from 'gatsby-env-variables'
import { ApiResponse } from '../http'
import {
  AnalyticsTimeSeriesData,
  CommonParams,
  StatisticsCountData,
  StatisticsTrendData,
  StatisticsCountByGroupData,
  StatLevelInfo,
  StatEndpointInfo,
  DataAvailability,
} from './analytics.interface'
import generateQueryParams from './utils/generateQueryParams'
import omit from 'lodash/omit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { devicesApi } from '../devices'

export const transformStatsErrorResponse = (
  response: FetchBaseQueryError,
): FetchBaseQueryError & { message?: string } => {
  if (response.status === 'FETCH_ERROR') {
    return { ...response, message: 'Analytics backend is not reachable from your network.' }
  }
  return response
}

const TIME_SERIES_PATH = '/v2/statistic/timeseries'
const COUNT_PATH = '/v2/statistic/count'
const TREND_PATH = '/v2/statistic/trend'
const DATA_AVAILABILITY_PATH = '/v2/data-availability'

export const analyticsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getStatisticsTimeSeries: builder.query({
      query: (params: CommonParams) => {
        const queryParams = generateQueryParams(omit(params, 'region', 'group'))

        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${TIME_SERIES_PATH}/${
            params.group ? params.group : ''
          }?${queryParams.toString()}`,
        )
      },
      transformResponse: (response: ApiResponse<AnalyticsTimeSeriesData>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),
    getStatisticsCount: builder.query({
      query: (params: CommonParams) => {
        const queryParams = generateQueryParams(omit(params, 'region'))

        return getQueryArgs(
          `https://${
            params.region
          }.${ANALYTICS_BASE_HOST_NAME}${COUNT_PATH}?${queryParams.toString()}`,
        )
      },
      transformResponse: (response: ApiResponse<StatisticsCountData>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),
    getStatisticsCountByGroup: builder.query({
      query: (params: CommonParams) => {
        const queryParams = generateQueryParams(omit(params, 'region', 'group'))

        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${COUNT_PATH}/${
            params.group
          }?${queryParams.toString()}`,
        )
      },
      transformResponse: (response: ApiResponse<StatisticsCountByGroupData>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),
    /**
     * We do not pass baselineStartTime and baselineEndTime to the api because
     * we use the default values for them. If they are not passed, the api will
     * use baselineEndTime = startTime and baselineStartTime = baselineEndTime - (endtime-starttime).
     */
    getStatisticsTrend: builder.query({
      query: (params: CommonParams) => {
        const queryParams = generateQueryParams(omit(params, 'region', 'group'))

        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${TREND_PATH}/${
            params.group
          }?${queryParams.toString()}`,
        )
      },
      transformResponse: (response: ApiResponse<StatisticsTrendData>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),
    createClientAlias: builder.mutation({
      query: ({
        region,
        deviceId,
        clientId,
        alias,
      }: {
        region: string
        deviceId: string
        clientId: string
        alias: string
      }) => {
        return getQueryArgs(
          `https://${region}.${ANALYTICS_BASE_URL}/client/alias?deviceId=${deviceId}&clientId=${clientId}`,
          'POST',
          JSON.stringify(alias),
        )
      },
      transformResponse: (response: ApiResponse<[]>) => response.body,
      transformErrorResponse,
      async onQueryStarted({ deviceId, clientId, alias }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const device = draft?.devices.find(d => d.PK === deviceId)

              if (device) {
                const client = device.clients?.find(c => c.id === clientId)

                if (client) {
                  client.alias = alias
                }
              }
            }),
          )

          if (deviceId) {
            dispatch(
              devicesApi.util.updateQueryData('getDeviceClients', deviceId, draft => {
                const client = draft?.clients?.find(c => c.id === clientId)

                if (client) {
                  client.alias = alias
                }
              }),
            )
          }
        } catch {}
      },
    }),
    deleteClientAlias: builder.mutation({
      query: ({
        region,
        deviceId,
        clientId,
      }: {
        region: string
        deviceId: string
        clientId: string
      }) => {
        return getQueryArgs(
          `https://${region}.${ANALYTICS_BASE_URL}/client/alias?deviceId=${deviceId}&clientId=${clientId}`,
          'DELETE',
        )
      },
      transformResponse: (response: ApiResponse<[]>) => response.body,
      transformErrorResponse,
      async onQueryStarted({ deviceId, clientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const device = draft?.devices.find(d => d.PK === deviceId)

              if (device) {
                const client = device.clients?.find(c => c.id === clientId)

                if (client) {
                  client.alias = undefined
                }
              }
            }),
          )

          if (deviceId) {
            dispatch(
              devicesApi.util.updateQueryData('getDeviceClients', deviceId, draft => {
                const client = draft?.clients?.find(c => c.id === clientId)

                if (client) {
                  client.alias = undefined
                }
              }),
            )
          }
        } catch {}
      },
    }),
    getDataAvailability: builder.query({
      query: (region: string) => {
        return getQueryArgs(
          `https://${region}.${ANALYTICS_BASE_HOST_NAME}${DATA_AVAILABILITY_PATH}`,
        )
      },
      transformResponse: (response: ApiResponse<DataAvailability>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),
    getStatEndpoints: builder.query({
      query: () => getQueryArgs('/analytics/endpoints'),
      transformErrorResponse,
      transformResponse: (response: ApiResponse<{ endpoints: StatEndpointInfo[] }>) =>
        response.body,
    }),
    getStatLevels: builder.query({
      query: () => getQueryArgs('/analytics/levels'),
      transformErrorResponse,
      transformResponse: (response: ApiResponse<{ levels: StatLevelInfo[] }>) => response.body,
    }),
  }),
})

export const {
  useGetStatisticsTimeSeriesQuery,
  useGetStatisticsCountQuery,
  useGetStatisticsCountByGroupQuery,
  useGetStatisticsTrendQuery,
  useGetStatEndpointsQuery,
  useGetStatLevelsQuery,
  useCreateClientAliasMutation,
  useDeleteClientAliasMutation,
  useGetDataAvailabilityQuery,
} = analyticsApi
